import * as React from 'react';

import classnames from 'classnames';

import {ActionBox} from '../action-box/action-box';
import {Button} from '../button/button';
import {ButtonCard} from '../card/button-card';
import {colors} from '../colors/colors';
import {Dialog} from '../dialog/dialog';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from '../icon/svg/loading-icon';
import type {LocationEnum} from '../popover/popover';
import {Switch} from '../switch/switch';
import {Body} from '../typography/body';

import styles from './integration-widget.module.css';

export type IntegrationWidgetStatus = 'initial' | 'enabled' | 'disabled' | 'enabling' | 'disabling';

type Props = {
    /** If enabled or disabled, what account is this for (usually an email or username) */
    account: string | null | undefined;
    /** Status of the integration.  Initial means never connected. */
    status: IntegrationWidgetStatus;
    /** Override the default "Enabled"/"Disabled" status */
    statusMessage?: string;
    /** String to display as button card when never before enabled */
    initialText?: string;
    logo: React.ReactElement<any>;
    onConnectClick: () => void;
    onDisconnectClick: () => void;
    /** Show a total account revoke button. Only shows if the integration is disabled */
    onRevokeClick?: () => void;
    /** Warning to show before revoking */
    revokeWarning?: string;
    revokeWarningLocation?: LocationEnum;
    actionStyle: 'button' | 'switch';
    errors?: string[];
    onlyDisable?: boolean;
    onlyDisconnect?: boolean;
    dialogButtonText?: string;
    disabled?: boolean;
    /** We are adding this to specifically deal with GOOGLE_APPS. Since we don't have an option
     * to disconnect if we are connect to apps, we are just hiding the option for now */
    hideForgetButton?: boolean;
    fullWidthInitialButton?: boolean;
    connectOnMount?: boolean;
    centerContent?: boolean;
    compact?: boolean;
    borderVariant?: 'default' | 'ai' | 'feature';
};

IntegrationWidget.defaultProps = {
    actionStyle: 'button',
};

export function IntegrationWidget(props: Props) {
    const [revokeWarningToShow, setRevokeWarningToShow] = React.useState<string | null | undefined>(
        null
    );
    const revokeButtonRef = React.useRef(null);
    const {status, statusMessage, revokeWarning, onRevokeClick, connectOnMount, onConnectClick} =
        props;
    const hasConnectedOnMount = React.useRef(false);

    React.useEffect(() => {
        if (connectOnMount && !hasConnectedOnMount.current) {
            hasConnectedOnMount.current = true;
            onConnectClick();
        }
    }, [connectOnMount, onConnectClick]);

    if (status === 'initial') {
        return (
            <ButtonCard
                isDisabled={props.disabled}
                onClick={onConnectClick}
                isFullWidth={props.fullWidthInitialButton}
                borderVariant={props.borderVariant}
            >
                <div
                    className={classnames('flex align-center user-select-none', {
                        'px-16 py-8': props.compact,
                        'pad-16': !props.compact,
                        'justify-center': props.centerContent,
                    })}
                >
                    <div className={styles.logo}>{props.logo}</div>
                    <Body>{props.initialText}</Body>
                </div>
            </ButtonCard>
        );
    }

    const enabledText = props.onlyDisconnect ? 'Disconnect' : 'Disable';

    const isEnabled = status === 'enabled';
    let buttonText = '';
    const isEnablingOrDisabling = status === 'enabling' || status === 'disabling';

    if (status === 'enabling') {
        buttonText = 'Enabling…';
    } else if (status === 'disabling') {
        buttonText = 'Disabling…';
    } else {
        buttonText = isEnabled ? enabledText : 'Enable';
    }

    let subtitle;

    if (statusMessage === undefined) {
        subtitle = isEnabled || status === 'disabling' ? 'Enabled' : 'Disabled';
    } else {
        subtitle = statusMessage;
    }

    let disconnectButton = null;

    if (props.onlyDisable && onRevokeClick) {
        disconnectButton = (
            <div className={styles['disconnect-button-small']}>
                <Button
                    getButtonRef={revokeButtonRef}
                    variant='text-danger'
                    size='xlarge'
                    onClick={() => {
                        if (revokeWarning) {
                            setRevokeWarningToShow(revokeWarning);
                        } else {
                            onRevokeClick();
                        }
                    }}
                >
                    Disable
                </Button>
            </div>
        );
    } else {
        disconnectButton = (
            <Button
                variant={isEnabled || status === 'disabling' ? 'text-danger' : 'primary'}
                size='xlarge'
                disabled={status === 'disabling' || status === 'enabling'}
                onClick={isEnabled ? props.onDisconnectClick : onConnectClick}
            >
                {buttonText}
            </Button>
        );
    }

    return (
        <React.Fragment>
            <ActionBox
                title={props.account || ''}
                shouldTruncate={true}
                subtitle={subtitle}
                icon={props.logo}
                errors={props.errors}
                actionElement={
                    props.actionStyle === 'button' ? (
                        disconnectButton
                    ) : (
                        <div className={styles['switch-group']}>
                            {isEnablingOrDisabling ? (
                                <LoadingIcon fill={colors.offWhiteDk} size={24} />
                            ) : undefined}
                            <Switch
                                isDisabled={status === 'disabling' || status === 'enabling'}
                                isOn={isEnabled || status === 'enabling'}
                                onChange={isEnabled ? props.onDisconnectClick : onConnectClick}
                            />
                        </div>
                    )
                }
            />
            {onRevokeClick &&
            (status === 'disabled' || status === 'enabling') &&
            !props.onlyDisable &&
            !props.hideForgetButton ? (
                <div className={styles['button-container']}>
                    <Button
                        getButtonRef={revokeButtonRef}
                        variant='text-primary'
                        onClick={() => {
                            if (revokeWarning) {
                                setRevokeWarningToShow(revokeWarning);
                            } else {
                                onRevokeClick();
                            }
                        }}
                        size='big'
                    >
                        Forget this account and add a new one
                    </Button>
                </div>
            ) : undefined}
            {revokeWarningToShow ? (
                <Dialog
                    anchor={revokeButtonRef.current}
                    isWarning={true}
                    question={revokeWarningToShow}
                    onPrimary={props.onRevokeClick}
                    onSecondary={() => {
                        setRevokeWarningToShow(null);
                    }}
                    primaryText={props.dialogButtonText || 'Disconnect'}
                    secondaryText='Cancel'
                    location={props.revokeWarningLocation || 'bottom'}
                />
            ) : null}
        </React.Fragment>
    );
}
